import React, { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../config/firebase';

const FIXED_QUESTION_COUNT = 5;

/**
 * RateWorkerModal — HC-RATING-004. Employer rates the worker on a hired
 * application. Self-contained: fetches its own question set and submits
 * itself, mirroring I9DatePicker.js's shape (self-fetching/self-submitting,
 * calls a callback on success) rather than depending on parent state.
 *
 * config/ratingQuestions is a role-keyed { worker: [...], employer: [...] }
 * doc (HC-RATING-004 phase 1) — this always reads the "employer" array,
 * since the employer is the rater here. Each question's `text` is a 6-
 * language locale map, but web-admin has no i18n system (confirmed zero
 * ARB/i18next anywhere in web-admin — see hc-rating-004-i18n-investigation.md)
 * so only `text.en` is ever read; all other UI chrome here is hardcoded
 * English too, consistent with the rest of this codebase.
 *
 * Props:
 *   applicationId {string}
 *   workerName    {string}
 *   onClose       {fn}         — called any time the modal should close
 *   onSuccess     {fn}         — called once the rating is submitted (or the
 *                                server reports it was already submitted),
 *                                so the caller can hide the Rate action
 */
function RateWorkerModal({ applicationId, workerName, onClose, onSuccess }) {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [scores, setScores] = useState({});
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // 'idle' | 'success' | 'notYetEligible' | 'alreadySubmitted' | 'error'
  const [submitStatus, setSubmitStatus] = useState('idle');

  useEffect(() => {
    let cancelled = false;

    const loadQuestions = async () => {
      setLoading(true);
      setLoadError(false);
      try {
        const snap = await getDoc(doc(db, 'config', 'ratingQuestions'));
        const raw = snap.data()?.employer;
        if (!cancelled) {
          if (Array.isArray(raw) && raw.length === FIXED_QUESTION_COUNT) {
            setQuestions(raw);
          } else {
            setLoadError(true);
          }
        }
      } catch (err) {
        console.error('RateWorkerModal: error loading config/ratingQuestions:', err);
        if (!cancelled) setLoadError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadQuestions();
    return () => { cancelled = true; };
  }, []);

  const canSubmit =
    !loading &&
    !loadError &&
    questions.length === FIXED_QUESTION_COUNT &&
    questions.every((q) => scores[q.questionId] != null);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const answers = questions.map((q) => ({ questionId: q.questionId, value: scores[q.questionId] }));
      const functions = getFunctions();
      const submitRating = httpsCallable(functions, 'submitRating');
      const trimmedComment = comment.trim();
      await submitRating({
        applicationId,
        answers,
        ...(trimmedComment ? { comment: trimmedComment } : {}),
      });
      setSubmitStatus('success');
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error('RateWorkerModal: submitRating error:', err);
      if (err.code === 'functions/failed-precondition') {
        setSubmitStatus('notYetEligible');
      } else if (err.code === 'functions/already-exists') {
        setSubmitStatus('alreadySubmitted');
        // The server says it's already submitted — that's the real state
        // regardless of what this client thought, so the caller should
        // still hide the Rate action.
        if (onSuccess) onSuccess();
      } else {
        setSubmitStatus('error');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (submitStatus === 'success' || submitStatus === 'alreadySubmitted') {
    return (
      <div style={styles.successBox}>
        <span style={styles.successIcon}>&#10003;</span>
        <span>
          {submitStatus === 'alreadySubmitted'
            ? 'You have already rated this worker.'
            : 'Thank you! Your rating has been submitted.'}
        </span>
        <button onClick={onClose} style={{ ...styles.button, marginTop: '12px' }}>
          Close
        </button>
      </div>
    );
  }

  if (submitStatus === 'notYetEligible') {
    return (
      <div style={styles.container}>
        <p style={styles.errorText}>
          This rating isn&apos;t available yet. Please check back later.
        </p>
        <button onClick={onClose} style={styles.button}>Close</button>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={styles.container}>
        <p style={styles.label}>Loading questions…</p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div style={styles.container}>
        <p style={styles.errorText}>Couldn&apos;t load rating questions. Please try again.</p>
        <button onClick={onClose} style={styles.button}>Close</button>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <h3 style={styles.heading}>Rate {workerName || 'Worker'}</h3>

      {questions.map((q) => (
        <div key={q.questionId} style={styles.questionRow}>
          <label style={styles.label}>{q.text?.en || ''}</label>
          <StarRatingInput
            value={scores[q.questionId]}
            onChange={(value) => setScores((prev) => ({ ...prev, [q.questionId]: value }))}
          />
        </div>
      ))}

      <label style={styles.label} htmlFor="rate-worker-comment">
        Additional comments (optional)
      </label>
      <p style={styles.privacyNote}>
        Your comment stays private — only you and this worker can see it.
      </p>
      <textarea
        id="rate-worker-comment"
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder="Share more about your experience..."
        rows={4}
        style={styles.textarea}
        disabled={submitting}
      />

      {submitStatus === 'error' && (
        <p style={styles.errorText}>Something went wrong. Please try again.</p>
      )}

      <div style={{ display: 'flex', gap: '8px' }}>
        <button
          onClick={handleSubmit}
          disabled={!canSubmit || submitting}
          style={
            !canSubmit || submitting
              ? { ...styles.button, ...styles.buttonDisabled }
              : styles.button
          }
        >
          {submitting ? 'Submitting…' : 'Submit Rating'}
        </button>
        <button onClick={onClose} disabled={submitting} style={styles.cancelButton}>
          Cancel
        </button>
      </div>
    </div>
  );
}

/** No existing rating-input precedent anywhere in web-admin (HC-RATING-000
 * removed the old star display, leaving nothing to reuse — confirmed in
 * hc-rating-004-employer-trigger-investigation.md) — new, small, local to
 * this file since it's only ever used here. */
function StarRatingInput({ value, onChange }) {
  return (
    <div style={{ display: 'flex', gap: '4px' }}>
      {[1, 2, 3, 4, 5].map((starValue) => {
        const filled = value != null && starValue <= value;
        return (
          <span
            key={starValue}
            onClick={() => onChange(starValue)}
            role="button"
            aria-label={`${starValue} star${starValue === 1 ? '' : 's'}`}
            aria-pressed={filled}
            style={{
              cursor: 'pointer',
              fontSize: '22px',
              lineHeight: 1,
              color: filled ? '#d97706' : '#cbd5e0',
              userSelect: 'none',
            }}
          >
            &#9733;
          </span>
        );
      })}
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    padding: '20px',
    background: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    maxWidth: '420px',
  },
  heading: {
    margin: 0,
    fontSize: '16px',
    fontWeight: '600',
    color: '#1a202c',
  },
  questionRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  label: {
    fontSize: '14px',
    color: '#4a5568',
    fontWeight: '500',
  },
  privacyNote: {
    margin: 0,
    fontSize: '12px',
    color: '#718096',
  },
  textarea: {
    padding: '8px 12px',
    fontSize: '14px',
    border: '1px solid #cbd5e0',
    borderRadius: '6px',
    outline: 'none',
    color: '#1a202c',
    resize: 'vertical',
    fontFamily: 'inherit',
  },
  errorText: {
    margin: 0,
    fontSize: '13px',
    color: '#e53e3e',
  },
  button: {
    padding: '10px 16px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#ffffff',
    background: '#3182ce',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  buttonDisabled: {
    background: '#a0aec0',
    cursor: 'not-allowed',
  },
  cancelButton: {
    padding: '10px 16px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#4a5568',
    background: '#ffffff',
    border: '1px solid #cbd5e0',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  successBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '8px',
    padding: '20px',
    background: '#f0fff4',
    border: '1px solid #9ae6b4',
    borderRadius: '8px',
    fontSize: '14px',
    color: '#276749',
    fontWeight: '500',
    maxWidth: '420px',
  },
  successIcon: {
    fontSize: '18px',
    color: '#38a169',
  },
};

export default RateWorkerModal;
